import React from 'react';
import PropTypes from 'prop-types';

/**
 * Input Component - Design System
 * 
 * A reusable input component with label, error states, and multiple input types.
 * Follows the dark theme design system with consistent styling and accessibility.
 * 
 * @component
 * @example
 * // Text input with label
 * <Input
 *   type="text"
 *   label="Full Name"
 *   placeholder="Enter your name"
 *   value={name}
 *   onChange={(e) => setName(e.target.value)}
 * />
 * 
 * // Input with error
 * <Input
 *   type="email"
 *   label="Email"
 *   value={email}
 *   error="Please enter a valid email"
 *   required
 * />
 */
const Input = ({
  type = 'text',
  label = '',
  placeholder = '',
  error = '',
  disabled = false,
  required = false,
  value,
  onChange,
  className = '',
  id,
  name,
  ...props
}) => {
  // Generate unique ID if not provided
  const inputId = id || `input-${name || type}-${Math.random().toString(36).substr(2, 9)}`;

  // Base input styles with dual-theme support
  const baseStyles = `
    w-full px-4 py-2
    bg-light-primary dark:bg-brand-surface
    border rounded-input
    text-light-text-primary dark:text-brand-text-primary
    placeholder-light-text-muted dark:placeholder-brand-text-muted
    focus:outline-none focus:ring-2 focus:border-transparent
    disabled:opacity-50 disabled:cursor-not-allowed
    transition-all duration-slow ease-in-out
  `;

  // Border and ring styles based on error state (dual-theme)
  const stateStyles = error
    ? `
      border-light-semantic-error dark:border-brand-error
      focus:ring-light-semantic-error dark:focus:ring-brand-error
    `
    : `
      border-light-border-default dark:border-brand-border-default
      focus:ring-light-accent-red dark:focus:ring-brand-accent-red
    `;

  return (
    <div className={`${className}`.trim()}>
      {/* Label */}
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-light-text-secondary dark:text-brand-text-secondary mb-2"
        >
          {label}
          {required && <span className="text-light-semantic-error dark:text-brand-error ml-1">*</span>}
        </label>
      )}

      {/* Input field */}
      <input
        id={inputId}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={`
          ${baseStyles}
          ${stateStyles}
        `.trim().replace(/\s+/g, ' ')}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${inputId}-error` : undefined}
        {...props}
      />

      {/* Error message */}
      {error && (
        <p
          id={`${inputId}-error`}
          className="mt-1 text-sm text-light-semantic-error dark:text-brand-error"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};

Input.propTypes = {
  /** Input type */
  type: PropTypes.oneOf(['text', 'email', 'password', 'number', 'tel', 'file']),
  /** Input label */
  label: PropTypes.string,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Error message to display */
  error: PropTypes.string,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Required field indicator */
  required: PropTypes.bool,
  /** Input value */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Change handler */
  onChange: PropTypes.func,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Input ID */
  id: PropTypes.string,
  /** Input name */
  name: PropTypes.string,
};

export default Input;
